import { usePositionsFilter } from '@src/components/Filters/FilterBuilder/usePositionsFilter';
import {
  BlotterDensity,
  type Column,
  type ColumnDef,
  columnTypes,
  type DateRangeFilter,
  POSITION,
  type Position,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  usePersistedBlotterTable,
  useWLHomeCurrency,
  useWsBlotterTable,
  type WebsocketRequest,
} from '@talos/kyoko';
import { pick } from 'lodash-es';
import { useMemo } from 'react';

const getColumns = (homeCurrency: string) =>
  [
    { type: 'account', field: 'MarketAccount', initialWidth: 200, initialRowGroup: true },
    { type: 'asset', field: 'Asset' },
    { type: 'size', field: 'Amount', params: { currencyField: 'DeltaCurrency' } },
    {
      type: 'size',
      field: 'Equivalent.Amount',
      params: { currencyField: 'Equivalent.Currency' },
      title: { intlKey: 'amountHomeCurrency' },
      titleIntlKeyValues: { homeCurrency },
      aggregate: true,
    },
    { type: 'size', field: 'CumFees', params: { currencyField: 'FeeCurrency' }, initialHide: true },
    { type: 'text', field: 'FeeCurrency', initialHide: true },
    { type: 'size', field: 'Delta', params: { currencyField: 'DeltaCurrency' }, aggregate: true },
    { type: 'text', field: 'DeltaCurrency', initialHide: true },
    {
      type: 'size',
      field: 'Equivalent.Delta',
      params: { currencyField: 'Equivalent.Currency' },
      title: { intlKey: 'deltaHomeCurrency' },
      titleIntlKeyValues: { homeCurrency },
      aggregate: true,
    },
    { type: 'size', field: 'MarkPrice', params: { currencyField: 'MarkPriceCurrency' } },
    { type: 'text', field: 'MarkPriceCurrency', initialHide: true },
    {
      type: 'size',
      field: 'Equivalent.MarkPrice',
      params: { currencyField: 'Equivalent.Currency' },
      title: { intlKey: 'markPriceHomeCurrency' },
      titleIntlKeyValues: { homeCurrency },
      aggregate: true,
    },
  ] satisfies ColumnDef<Position>[];

export const usePositionsBlotter = () => {
  const { homeCurrency } = useWLHomeCurrency();
  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        suppressColumnsToolPanel: true,
        params: { suppressCount: true },
        sortable: true,
        initialWidth: 250,
      }),
    []
  );

  const columns: Column[] = useMemo(() => getColumns(homeCurrency), [homeCurrency]);

  const persistedBlotterTable = usePersistedBlotterTable('Positions_Blotter');

  const filteredPositions = usePositionsFilter({ persistedBlotterTable });
  const { blotterTableFilterProps, filterBuilderProps, clientSideFilter: clientLocalFilter } = filteredPositions;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: false },
    filterBuilderProps,
  });

  const request = useMemo<WebsocketRequest>(
    () => ({
      name: POSITION,
      tag: 'BLOTTER_POSITIONS',
      EquivalentCurrency: homeCurrency,
      ...removeEmptyFilters(onlyServerFilterKeys(filteredPositions.filter)),
    }),
    [homeCurrency, filteredPositions.filter]
  );

  const blotterTable = useWsBlotterTable<WebsocketRequest, Position>({
    request,
    rowID: 'rowID' satisfies keyof Position,
    density: BlotterDensity.Comfortable,
    clientLocalFilter,
    persistence: persistedBlotterTable,
    columns,
    gridOptions: { autoGroupColumnDef },
  });

  return useMemo(
    () => ({ blotterTable, blotterTableFilterProps, filterBuilderAccordion }),
    [blotterTable, blotterTableFilterProps, filterBuilderAccordion]
  );
};

function onlyServerFilterKeys(filter: DateRangeFilter | undefined) {
  if (!filter) {
    return filter;
  }
  return pick(filter, ['EndDate', 'StartDate', 'MarketAccounts']);
}
